import {
  createCalendar,
  endOfMonth,
  endOfWeek,
  getWeeksInMonth,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  today,
  type CalendarDate,
  type DateDuration,
} from "@internationalized/date";
import { useLocale, useTranslations } from "next-intl";
import { useRef, type ReactNode } from "react";
import { useCalendarCell, useCalendarGrid, useRangeCalendar } from "react-aria";
import { useRangeCalendarState, type RangeCalendarState } from "react-stately";

import { TIMEZONE } from "@trip/constants";
import { cn, type DateRange } from "@trip/utils";

import { AngleLeftIcon } from "~/components/icons/angle-left";
import { AngleRightIcon } from "~/components/icons/angle-right";
import { CloseIcon } from "~/components/icons/close-icon";
import { Button } from "~/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";

import { WithSearchFocus } from "../search/_components/search-focus";

function CalendarCell({
  state,
  date,
  currentMonth,
  locale,
}: {
  currentMonth: CalendarDate;
  date: CalendarDate;
  locale: string;
  state: RangeCalendarState;
}) {
  const ref = useRef(null);
  const {
    cellProps,
    buttonProps,
    isSelected,
    isOutsideVisibleRange,
    isDisabled,
    isUnavailable,
    formattedDate,
  } = useCalendarCell({ date }, state, ref);

  const isOutsideMonth = !isSameMonth(currentMonth, date);

  // Is this a bug?
  const range = state.highlightedRange as unknown as
    | typeof state.highlightedRange
    | null;

  const isLeftRound =
    (isSelected &&
      (isSameDay(startOfWeek(date, locale), date) ||
        isSameDay(startOfMonth(date), date))) ||
    (range ? date.compare(range.start) === 0 : false);
  const isRightRound =
    (isSelected &&
      (isSameDay(endOfWeek(date, locale), date) ||
        isSameDay(endOfMonth(date), date))) ||
    (range ? date.compare(range.end) === 0 : false);

  const isEndOrStart = range
    ? isSameDay(range.start, date) || isSameDay(range.end, date)
    : false;

  return (
    <td {...cellProps} className="relative min-w-[32px] pt-px">
      {isEndOrStart && !isOutsideVisibleRange && !isOutsideMonth ? (
        <div className="bg-prune-500 text-prune-200 pointer-events-none absolute inset-0 flex aspect-square flex-col items-center justify-center rounded-full">
          {formattedDate}
        </div>
      ) : null}
      <button
        {...buttonProps}
        type="button"
        className={cn(
          "flex aspect-square size-full flex-col items-center justify-center",
          isLeftRound && isRightRound ? "rounded-full" : "",
          isLeftRound ? "rounded-l-full" : "",
          isRightRound ? "rounded-r-full" : "",
          isOutsideMonth
            ? "invisible"
            : [
                isSelected ? "bg-prune-200 text-prune-500 font-medium" : "",
                isDisabled ? "opacity-20" : "",
                isUnavailable ? "opacity-20" : "",
              ],
        )}
        hidden={isOutsideVisibleRange}
        ref={ref}
      >
        {formattedDate}
      </button>
    </td>
  );
}

export function CalendarGrid({
  state,
  locale,
  offset,
}: {
  locale: string;
  offset?: DateDuration;
  state: RangeCalendarState;
}) {
  const startDate = state.visibleRange.start.add(offset ?? {});
  const endDate = endOfMonth(startDate);

  const { gridProps, headerProps, weekDays } = useCalendarGrid(
    { endDate, startDate },
    state,
  );

  // Get the number of weeks in the month so we can render the proper number of rows.
  const weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale);

  return (
    <div>
      <div className="mb-4 mt-0.5 text-center font-medium leading-relaxed">
        {startDate.toDate(state.timeZone).toLocaleDateString(locale, {
          day: undefined,
          month: "long",
          year: "numeric",
        })}
      </div>
      <table
        {...gridProps}
        cellPadding={0}
        cellSpacing={0}
        className="border-collapse border-spacing-0 text-sm"
      >
        <thead {...headerProps} className="text-neutral-500">
          <tr>
            {weekDays.map((day, index) => (
              // eslint-disable-next-line @eslint-react/no-array-index-key
              <th key={`${index}-${day}`}>{day}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: weeksInMonth }, (_, weekIndex) => (
            <tr key={weekIndex}>
              {state
                .getDatesInWeek(weekIndex, startDate)
                .map((date) =>
                  date ? (
                    <CalendarCell
                      currentMonth={startDate}
                      date={date}
                      key={date.toString()}
                      locale={locale}
                      state={state}
                    />
                  ) : (
                    <td key={null} />
                  ),
                )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Calendar({
  value,
  onChange,
  hasFocus,
  onFocus,
  children,
}: {
  children: ReactNode;
  hasFocus: boolean;
  onChange: (value: DateRange | null) => void;
  onFocus: () => void;
  value: DateRange | null;
}) {
  const t = useTranslations("Search");
  const tAccessibility = useTranslations("Accessibility");
  const props = {
    minValue: today(TIMEZONE).add({ days: 1 }),
    onChange,
    value,
  };

  const locale = useLocale();
  const state = useRangeCalendarState({
    ...props,
    createCalendar,
    locale,
    pageBehavior: "single",
    visibleDuration: { months: 2 },
  });

  const ref = useRef<HTMLDivElement>(null);
  const { calendarProps } = useRangeCalendar(props, state, ref);

  return (
    <Popover
      onOpenChange={(open) => {
        if (open) {
          onFocus();
        }
      }}
    >
      <span className="sr-only">{tAccessibility("calendar")}</span>
      <div className="relative basis-[290px]">
        {hasFocus && value ? (
          <Button
            size="circle"
            variant="ghost"
            className="absolute right-0 top-1/2 z-30 -translate-y-1/2 text-neutral-500 hover:bg-transparent hover:text-neutral-900"
            onClick={(event) => {
              event.preventDefault();
              onChange(null);
            }}
            aria-label="Close"
          >
            <CloseIcon className="size-5" />
          </Button>
        ) : null}
        <PopoverTrigger asChild>
          <button type="button" className="w-full" aria-label={t("when")}>
            <WithSearchFocus hasFocus={hasFocus}>
              <div className="text-xs font-medium">{t("when")}</div>
              <div>{children}</div>
            </WithSearchFocus>
          </button>
        </PopoverTrigger>
      </div>
      <PopoverContent
        className="shadow-card-search w-auto rounded-3xl border-none"
        sideOffset={8}
      >
        <div {...calendarProps} className="inline-block" ref={ref}>
          <div className="absolute inset-x-0 top-0 flex w-full items-center justify-between">
            {/* <h2 className="ml-2 flex-1 text-xl font-bold">{title}</h2> */}
            <button
              type="button"
              className="mt-1 p-4"
              onClick={() => {
                state.focusPreviousPage();
              }}
            >
              <AngleLeftIcon />
            </button>
            <button
              type="button"
              className="mt-1 p-4"
              onClick={() => {
                state.focusNextPage();
              }}
            >
              <AngleRightIcon />
            </button>
          </div>
          <div className="flex gap-4">
            <CalendarGrid locale={locale} state={state} />
            <CalendarGrid
              locale={locale}
              offset={{ months: 1 }}
              state={state}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
